import React, { useEffect, useRef } from 'react';
import { useThree, invalidate, ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { editorStore } from '../../store/editorStore';
import { observer } from 'mobx-react-lite';
import { projectToWorld } from '../FloorPlan/projectToWorld';
import { floorplannerStore } from '../../store/floorplannerStore';
import { SingleLineType, WallConnectionEnd, WallConnectionStart } from '../../types/floorplannerTypes';
import { runInAction } from 'mobx';
import { updateAlignmentLines } from '../FloorPlan/updateAligmentLines';
import { renderStore } from '../../store/renderStore';
import { applyAlignmentAfterSnap } from '../FloorPlan/applyAlignmentAfterSnap';

const LineConstructionPlane = observer(() => {
  const {
    lineConstructionMode,
    lineConstructionModeAddNewLine,
    view3D,
  } = editorStore;
  const { gl, camera } = useThree();
  const isActive = lineConstructionMode && !view3D;
  const addNewLine = useRef(false);
  const currentLine = useRef(undefined as string | undefined);
  const lastClickPosition = useRef(undefined as THREE.Vector2 | undefined);
  const firstClickPosition = useRef<THREE.Vector2 | undefined>(undefined);
  const previousLineId = useRef<string | undefined>(undefined);
  const firstLineId = useRef<string | undefined>(undefined);
  const pendingMove = useRef<{ position: THREE.Vector2; shiftKey: boolean } | null>(null);
  const rafId = useRef<number | null>(null);

  const getSnappedEndPosition = (start: THREE.Vector2, rawEnd: THREE.Vector2, enableAxisSnap: boolean): {
    position: THREE.Vector2;
    snappedAngle: number | undefined;
  } => {
    if (!enableAxisSnap) {
      return { position: rawEnd, snappedAngle: undefined };
    }

    const deltaX = rawEnd.x - start.x;
    const deltaY = rawEnd.y - start.y;
    const angle = Math.atan2(deltaY, deltaX) * (180 / Math.PI);
    const distance = start.distanceTo(rawEnd);
    const normalizedAngle = (angle + 360) % 360;
    const snapAngles = [0, 45, 90, 135, 180, 225, 270, 315, 360];
    const closestSnapAngle = snapAngles.reduce(
      (prev, curr) => Math.abs(curr - normalizedAngle) < Math.abs(prev - normalizedAngle) ? curr : prev,
    );
    const radianSnapAngle = closestSnapAngle * (Math.PI / 180);
    const snapEndX = start.x + distance * Math.cos(radianSnapAngle);
    const snapEndY = start.y + distance * Math.sin(radianSnapAngle);
    const snappedPosition = new THREE.Vector2(snapEndX, snapEndY);

    if (rawEnd.distanceTo(snappedPosition) < editorStore.interactionSnappingTolerance()) {
      // Return the snapped angle (normalized to 0-359)
      const normalizedSnapAngle = closestSnapAngle === 360 ? 0 : closestSnapAngle;
      return { position: snappedPosition, snappedAngle: normalizedSnapAngle };
    }

    return { position: rawEnd, snappedAngle: undefined };
  };

  const finalizeActiveLine = (line: SingleLineType) => {
    // CRITICAL: Use the line's CURRENT end position, not the click position.
    // The line already has the correct snapped position from the drag phase (processPointerMove).
    // Using the click position would cause the line to jump if the cursor moved slightly
    // between the last pointer move and the click, or if alignment snapping was active.
    let end = line.end.clone();
    let start = line.start.clone();

    // Check if we're closing the loop by looking at the first line's actual start position
    // Use the line's CURRENT end position (which may be positioned at connection from drag)
    const currentLineEnd = line.end;

    let closingToStart = false;
    let startLine: SingleLineType | undefined = undefined;

    if (firstLineId.current && firstLineId.current !== line.id) {
      startLine = floorplannerStore.singleLinesMap.get(firstLineId.current);
      if (startLine) {
        // Lines connect end-to-end at center points
        // Check if we're close to the first line's start
        const snapTolerance = editorStore.interactionSnappingTolerance();
        closingToStart = currentLineEnd.distanceTo(startLine.start) < snapTolerance ||
          end.distanceTo(startLine.start) < snapTolerance;
      }
    }

    // Fallback to firstClickPosition if firstLineId is not set
    if (!closingToStart && !startLine && firstClickPosition.current) {
      const snapTolerance = editorStore.interactionSnappingTolerance();
      closingToStart = end.distanceTo(firstClickPosition.current) < snapTolerance;
      if (closingToStart) {
        startLine = Array.from(floorplannerStore.singleLinesMap.values()).find(l =>
          l.start.distanceTo(end) < snapTolerance && l.id !== line.id
        );
        // If we can't find the start line, don't treat this as closing the loop
        if (!startLine) {
          closingToStart = false;
        }
      }
    }

    if (closingToStart && startLine) {
      // When closing the loop, connect end-to-end
      end = startLine.start.clone();
      floorplannerStore.updateObjectPosition(line.id, start.clone(), end.clone());
      
      // Establish connections for loop closing
      floorplannerStore.snapToOtherObjects(line.id, WallConnectionEnd, end.clone(), true);
      floorplannerStore.snapToOtherObjects(startLine.id, WallConnectionStart, startLine.start.clone(), true);
      floorplannerStore.objectNeedsUpdate(startLine.id);
      floorplannerStore.objectNeedsUpdate(line.id);
    } else {
      // Check for line connections at the CURRENT position without moving the line.
      // The line's position is already correct from the drag phase - we're just establishing connections.
      // We do NOT update the line position here, even if a nearby connection is found.
      floorplannerStore.snapToOtherObjects(line.id, WallConnectionEnd, end.clone(), true);
      // NOTE: We intentionally do NOT update the line position based on snap results.
      // The line should stay exactly where it was during the drag phase.
    }

    const updatedLine = floorplannerStore.singleLinesMap.get(line.id);
    if (updatedLine) {
      const lineLength = updatedLine.start.distanceTo(updatedLine.end);
      editorStore.setSingleLineEditingLengthPanel(lineLength);
    }

    floorplannerStore.objectNeedsUpdate(line.id);

    return { endPoint: end, closedLoop: !!closingToStart };
  };

  const handlePointerDown = (event: ThreeEvent<PointerEvent>) => {
    if (editorStore.lineConstructionMode) {
      event.stopPropagation();
      const { clientX, clientY } = event;
      const pos = projectToWorld(clientX, clientY, gl, camera);
      const clickPosition = new THREE.Vector2(pos[0], pos[1]);
      if (!firstClickPosition.current) {
        firstClickPosition.current = clickPosition.clone();
      }

      if (!currentLine.current) {
        lastClickPosition.current = clickPosition;
        addNewLine.current = true;
        return;
      }

      const line = floorplannerStore.singleLinesMap.get(currentLine.current);
      if (!line) {
        return;
      }

      const result = finalizeActiveLine(line);
      if (!result) {
        return;
      }

      lastClickPosition.current = result.endPoint.clone();
      previousLineId.current = line.id;

      if (result.closedLoop) {
        addNewLine.current = true;
        editorStore.setLineConstructionMode(false);
        return;
      }

      addNewLine.current = true;
      currentLine.current = undefined;
    }
  };

  const processPointerMove = () => {
    rafId.current = null;
    const move = pendingMove.current;
    pendingMove.current = null;
    if (!move || !editorStore.lineConstructionMode || editorStore.view3D) {
      return;
    }

    const pointerPosition = move.position;
    const distance = lastClickPosition.current?.distanceTo(pointerPosition) ?? 0;

    if (addNewLine.current && distance > 0.1 && lastClickPosition.current) {
      createNewLineSegment([pointerPosition.x, pointerPosition.y]);
    }

    // Update the line's position if it's already been created
    if (!addNewLine.current && currentLine.current) {
      const line = floorplannerStore.singleLinesMap.get(currentLine.current);
      if (!line) {
        return;
      }

      const start = line.start;

      // SNAPPING PRIORITY ORDER during line construction:
      // 1. Object connection snapping (end-to-end connections) - HIGHEST PRIORITY
      // 2. 45-degree angle snapping (when within tolerance of snap angle)
      // 3. Alignment lines - LOWEST PRIORITY

      let end = pointerPosition.clone();
      let snappedLineAngle: number | undefined = undefined;
      let objectSnapApplied = false;

      // PRIORITY 1: Object connection snapping (highest priority)
      // Check for object snap BEFORE angle snapping - connections take priority
      // Use preview snapping during drag (doesn't establish connections, just shows position)
      if (!move.shiftKey) {
        const centerSnapPoint = floorplannerStore.getSnappingPositionForDrag(line.id, WallConnectionEnd, pointerPosition);
        if (centerSnapPoint && pointerPosition.distanceTo(centerSnapPoint) < editorStore.interactionSnappingTolerance()) {
          end = centerSnapPoint.clone();
          objectSnapApplied = true; // Mark to skip angle snapping
        }
      }

      // PRIORITY 2: 45-degree angle snapping (only if no object snap was applied)
      if (!objectSnapApplied && !move.shiftKey) {
        const snapResult = getSnappedEndPosition(start, pointerPosition, true);
        end = snapResult.position;
        snappedLineAngle = snapResult.snappedAngle;
      }

      let finalPositions = { start: start.clone(), end: end.clone() };

      // PRIORITY 3: Alignment snapping (lowest priority)
      // Skip if object snap was applied (connection priority)
      // Pass the snapped line angle so that alignment axes within 20° of the line angle are filtered out
      const skipAlignment = objectSnapApplied;
      const alignmentResult = skipAlignment ? { applied: false, positions: null } : applyAlignmentAfterSnap({
        objectId: line.id,
        intendedPoint: finalPositions.end,
        connectionEnd: WallConnectionEnd,
        alignmentKey: "end",
        suppressSnapping: false,
        existingPositions: finalPositions,
        snappedWallAngle: snappedLineAngle,
      });

      if (alignmentResult.applied && alignmentResult.positions) {
        finalPositions = alignmentResult.positions;
      }

      floorplannerStore.updateObjectPosition(line.id, finalPositions.start, finalPositions.end);
      updateAlignmentLines(line.id, finalPositions.end.x, finalPositions.end.y, "end");

      const updatedLine = floorplannerStore.singleLinesMap.get(line.id);
      if (updatedLine) {
        const lineLength = updatedLine.start.distanceTo(updatedLine.end);
        editorStore.setSingleLineEditingLengthPanel(lineLength);
      }

      // Update connected lines if needed
      const startConnectedToId = floorplannerStore.objectConnectedTo(line.id, WallConnectionStart);
      if (startConnectedToId) {
        floorplannerStore.objectNeedsUpdate(startConnectedToId);
      }
    }
  };

  const handlePointerMove = (event: ThreeEvent<PointerEvent>) => {
    if (!editorStore.lineConstructionMode || editorStore.view3D) {
      return;
    }

    event.stopPropagation();
    document.body.classList.remove("cursor-vector");
    document.body.style.cursor = 'crosshair';
    const { clientX, clientY } = event;
    const pos = projectToWorld(clientX, clientY, gl, camera);
    pendingMove.current = {
      position: new THREE.Vector2(pos[0], pos[1]),
      shiftKey: event.shiftKey,
    };

    if (rafId.current === null) {
      rafId.current = requestAnimationFrame(processPointerMove);
    }
  };

  const handlePointerUp = () => {
  };

  const handleDoubleClick = (event: ThreeEvent<MouseEvent>) => {
    if (editorStore.lineConstructionMode) {
      // Get the click position in world coordinates
      const { clientX, clientY } = event;
      const pos = projectToWorld(clientX, clientY, gl, camera);
      const clickPosition = new THREE.Vector2(pos[0], pos[1]);

      // Check if there's a current line being constructed
      if (currentLine.current) {
        const line = floorplannerStore.singleLinesMap.get(currentLine.current);
        if (line) {
          const end = line.end;

          // Calculate the distance between the click and the line's endpoint
          const distance = clickPosition.distanceTo(end);

          // If the click is close enough to the endpoint, exit construction mode
          if (distance < editorStore.interactionSnappingTolerance()) {
            // Exit line construction mode
            editorStore.setLineConstructionMode(false);
            // Perform any additional cleanup if necessary
            editorStore.clearSelections();
            renderStore.clearAlignmentLines();
          }
        }
      }
    }
  };

  const createNewLineSegment = (pos: [number, number]) => {
    if (lastClickPosition.current) {
      let start = previousLineId.current ?
        floorplannerStore.singleLinesMap.get(previousLineId.current)?.end.clone() as THREE.Vector2 :
        lastClickPosition.current.clone();
      const end = new THREE.Vector2(pos[0], pos[1]);

      // Create a new line object
      const newLine: SingleLineType = {
        id: floorplannerStore.generateId(),
        type: "singleLine",
        start: start as THREE.Vector2,
        end: end as THREE.Vector2,
      };
      floorplannerStore.addSingleLine(newLine);

      // Track the first line for loop closing detection
      if (!firstLineId.current) {
        firstLineId.current = newLine.id;
      }

      // Connect to previous line if exists (end-to-end connection)
      if (previousLineId.current) {
        runInAction(() => {
          floorplannerStore.snapToOtherObjects(newLine.id, WallConnectionStart, start.clone(), true);
          floorplannerStore.snapToOtherObjects(previousLineId.current!, WallConnectionEnd, start.clone(), true);
        });
      }

      if (!editorStore.isShiftPressed) {
        editorStore.clearSelections();
      }
      editorStore.addSelection(newLine);
      invalidate();

      currentLine.current = newLine.id;
      addNewLine.current = false;
    }
  };

  // When the user press Enter key (like inside Edit field), editorStore.lineConstructionModeAddNewLine will signal to add a new line
  useEffect(() => {
    if (currentLine.current && lineConstructionModeAddNewLine) {
      editorStore.setLineConstructionModeAddNewLine(false);
      const line = floorplannerStore.singleLinesMap.get(currentLine.current);
      if (line) {
        lastClickPosition.current = line.end.clone();
        previousLineId.current = line.id;
        currentLine.current = undefined;
        addNewLine.current = true;
        if (!firstClickPosition.current) {
          firstClickPosition.current = line.start.clone();
        }
      }
    }
  }, [lineConstructionModeAddNewLine]);

  useEffect(() => {
    if (!isActive) {
      const hadConstructedLine = Boolean(
        currentLine.current || previousLineId.current || firstLineId.current,
      );
      // Collect line IDs that need finalization before clearing refs
      const linesToFinalize: string[] = [];

      if (currentLine.current && !addNewLine.current) {
        floorplannerStore.removeSingleLine(currentLine.current);
      } else if (currentLine.current && addNewLine.current) {
        // Current line was finalized, needs update
        linesToFinalize.push(currentLine.current);
      }
      if (previousLineId.current && !linesToFinalize.includes(previousLineId.current)) {
        linesToFinalize.push(previousLineId.current);
      }
      if (firstLineId.current && !linesToFinalize.includes(firstLineId.current)) {
        linesToFinalize.push(firstLineId.current);
      }

      floorplannerStore.setDeferRoomUpdates(false);
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }

      renderStore.setIsDragging(false);
      renderStore.clearAlignmentLines();
      editorStore.setSingleLineEditingLength(undefined);
      editorStore.setSingleLineEditingLengthPanel(undefined);

      // Finalize all lines after exiting construction mode
      linesToFinalize.forEach(lineId => {
        const line = floorplannerStore.singleLinesMap.get(lineId);
        if (line) {
          // Force a full geometry update
          floorplannerStore.objectNeedsUpdate(lineId);
        }
      });

      addNewLine.current = false;
      currentLine.current = undefined;
      lastClickPosition.current = undefined;
      firstClickPosition.current = undefined;
      previousLineId.current = undefined;
      firstLineId.current = undefined;
      document.body.style.cursor = 'auto';
      floorplannerStore.setBlockDirty(false);
      if (hadConstructedLine) {
        floorplannerStore.setDirty();
      }
      return;
    }

    floorplannerStore.setDeferRoomUpdates(true);
    renderStore.setIsDragging(true);
    document.body.classList.remove("cursor-vector");
    document.body.style.cursor = 'crosshair';
    floorplannerStore.setBlockDirty(true);
  }, [isActive, lineConstructionMode, view3D]);

  useEffect(() => {
    return () => {
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
      }
      floorplannerStore.setDeferRoomUpdates(false);
      renderStore.setIsDragging(false);
    };
  }, []);

  if (!isActive) {
    return null;
  }

  return (
    <mesh
      position={[0, 0, -0.01]} // Ensure the plane is in the background
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onDoubleClick={handleDoubleClick}
      userData={{ id: "line-construction-plane" }}
    >
      <planeGeometry args={[1000, 1000]} />
      <meshBasicMaterial transparent opacity={0} />
    </mesh>
  );
});

export default LineConstructionPlane;
